import React from 'react';
import Header from './Header';
import Footer from './Footer';

const DeleteAccount = () => {
  return (
    <div className="min-h-screen bg-offWhite text-charcoal">
      <Header />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-playfair font-bold mb-8 text-charcoal">How to Delete Your Account</h1>
        
        <div className="prose prose-lg max-w-none text-charcoal/80">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">Delete Your Wako Account</h2>
            <p className="mb-6">
              We understand that sometimes you may want to delete your Wako account. This guide will walk you through the process of permanently deleting your account and all associated data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">Before You Delete</h2>
            <p className="mb-4">
              Please note that deleting your account is a permanent action. Consider the following before proceeding:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>All your profile information, photos, and matches will be permanently deleted</li>
              <li>Your conversation history will be removed</li>
              <li>Any active subscriptions will be cancelled</li>
              <li>This action cannot be undone</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">Step-by-Step Deletion Guide</h2>
            
            <h3 className="text-xl font-semibold text-charcoal mb-3">On Mobile App</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-3">
              <li>Open the Wako app and log in to your account</li>
              <li>Tap the menu icon (hamburger menu) in the top-left corner</li>
              <li>Navigate to <strong>Settings</strong></li>
              <li>Select <strong>Account Settings</strong></li>
              <li>Scroll down and tap <strong>Delete Account</strong></li>
              <li>Review the warning message and tap <strong>Confirm Deletion</strong></li>
              <li>Enter your password to confirm</li>
              <li>Your account will be deleted immediately</li>
            </ol>

            <h3 className="text-xl font-semibold text-charcoal mb-3">On Web</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-3">
              <li>Log in to your Wako account on the website</li>
              <li>Click on your profile icon in the top-right corner</li>
              <li>Select <strong>Settings</strong> from the dropdown menu</li>
              <li>Click on <strong>Account Settings</strong></li>
              <li>Scroll to the bottom of the page</li>
              <li>Click <strong>Delete Account</strong> (usually in red)</li>
              <li>Read and acknowledge the deletion warning</li>
              <li>Enter your password to confirm the deletion</li>
              <li>Your account will be permanently deleted</li>
            </ol>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">What Happens After Deletion?</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Immediate:</strong> You will be logged out of all devices</li>
              <li><strong>Profile:</strong> Your profile will no longer be visible to other users</li>
              <li><strong>Data:</strong> All personal information will be deleted from our servers (except where required by law)</li>
              <li><strong>Matches:</strong> Existing matches will see your profile as deleted</li>
              <li><strong>Re-registration:</strong> You can create a new account with the same email after deletion</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">Account Deactivation Alternative</h2>
            <p className="mb-4">
              If you're not sure about permanently deleting your account, you can choose to <strong>deactivate</strong> it instead. Deactivation temporarily hides your profile but keeps your data intact. You can reactivate your account anytime within 30 days.
            </p>
            <p>
              To deactivate instead of delete, choose the deactivation option in Account Settings and follow the prompts.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">Privacy & Data Handling</h2>
            <p className="mb-4">
              When you delete your account:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>We immediately stop collecting data associated with your account</li>
              <li>Personal information is securely deleted from our primary servers</li>
              <li>Backup copies may take up to 30 days to be removed</li>
              <li>Anonymized usage data may be retained for analytics purposes</li>
              <li>You can request a copy of your data before deletion (see our Privacy Policy)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">Need Help?</h2>
            <p className="mb-4">
              If you encounter any issues deleting your account or have questions, please contact our support team:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>In-App Support:</strong> Use the help icon in your account settings</li>
              <li><strong>Response Time:</strong> We typically respond within 24 hours</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">Troubleshooting</h2>
            
            <h3 className="text-lg font-semibold text-charcoal mb-2">Can't find the delete option?</h3>
            <p className="mb-4">
              Make sure you're logged in and have the latest version of the app or are using an up-to-date browser. Clear your cache and try again.
            </p>

            <h3 className="text-lg font-semibold text-charcoal mb-2">Forgot your password?</h3>
            <p className="mb-4">
              You'll need your password to confirm account deletion. Use the password reset option on the login screen to regain access before deleting.
            </p>

            <h3 className="text-lg font-semibold text-charcoal mb-2">Still having trouble?</h3>
            <p>
              Contact our support team with your registered email address, and we'll assist you with the deletion process.
            </p>
          </section>

          <section className="mb-8 p-6 bg-peach/10 rounded-lg">
            <h3 className="text-lg font-semibold text-charcoal mb-2">Important Notice</h3>
            <p className="text-sm">
              Account deletion is permanent and irreversible. If you have an active subscription, you will not receive a refund. If you only wish to take a break from dating, consider deactivating your account instead.
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DeleteAccount;
